import React from 'react';
import {useAppSelector} from "../../redux/hooks";
import {useHasAnyRole} from "../../hooks/useHasAnyRole";
import PageWithHeader from "../common/PageWithHeader";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddVisitor from "./AddVisitor";
import InviteDetails from "./InviteDetails";
import {Invite, InviteData} from "../../redux/residentPortalApi";
import LoggedOutView from "../common/LoggedOutView";
import PreviousInvites from "./PreviousInvites";
import ScanInvite from "./ScanInvite";
import TodayVisitors from "./TodayVisitors";
import CurrentVisitors from "./CurrentVisitors";
import {addVisitorModelType, visitorTypeOptions} from "./addVisitorModel";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`visitor-tabpanel-${index}`}
      aria-labelledby={`visitor-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const supportedVisitorTypes = new Set(visitorTypeOptions.map((option) => option.value));
const legacyVisitorTypeMap: Record<string, addVisitorModelType['visitorType']> = {
  bnb: 'guest',
  company: 'service',
  visitor: 'guest',
};

const normalizeVisitorType = (visitorType?: string) => {
  if (!visitorType) {
    return 'guest';
  }

  if (supportedVisitorTypes.has(visitorType)) {
    return visitorType;
  }

  return legacyVisitorTypeMap[visitorType] || 'guest';
};

const VisitorManagementDashboard: React.FC = () => {
  const user = useAppSelector(state => state.auth.user)
  const isOperator = useHasAnyRole(['operator']);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const isVmsUser = user?.roles && user.roles.includes('vms');
  const canScanInvites = Boolean(isOperator || isVmsUser);

  const [showCreatedInvite, setShowCreatedInvite] = React.useState(false)
  const [createdInvite, setCreatedInvite] = React.useState<InviteData | null>(null);
  const [reinvitePrefill, setReinvitePrefill] = React.useState<Partial<addVisitorModelType> | null>(null);

  const tabDefs = [
    {key: 'create', label: 'Create Invite', visible: true},
    {key: 'previous', label: 'Previous Invites', visible: isOperator},
    {key: 'today', label: "Today's Visitors", visible: !!isVmsUser},
    {key: 'current', label: 'Current Visitors', visible: !!isVmsUser},
    {key: 'scan', label: 'Scan Invite', visible: isOperator || !!isVmsUser},
  ] as const;
  const visibleTabs = tabDefs.filter((tab) => tab.visible);
  const tabIndex = (key: (typeof tabDefs)[number]['key']) =>
    visibleTabs.findIndex((tab) => tab.key === key);

  // Set default tab value - VMS users see the Today's Visitors tab by default
  const [tabValue, setTabValue] = React.useState(() => (isVmsUser ? tabIndex('today') : 0));

  const handleSuccess = (invite: InviteData) => {
    setCreatedInvite(invite);
    setShowCreatedInvite(true);
    setReinvitePrefill(null);
  };

  const handleReinvite = (invite: Invite) => {
    const [firstName, ...rest] = (invite.visitorName || '').trim().split(' ');
    setReinvitePrefill({
      firstName: firstName || '',
      lastName: rest.join(' '),
      visitorEmail: invite.visitorEmail || '',
      visitorWhatsApp: invite.visitorWhatsApp || '',
      visitorType: normalizeVisitorType(invite.visitorType),
      purpose: invite.purpose || '',
      propertyId: invite.propertyId || '',
      // leave date/time blank so the user selects new timing for this reinvite
      isMultiDay: false,
      visitDateTime: '',
      startDate: '',
      endDate: '',
    });
    setShowCreatedInvite(false);
    setTabValue(tabIndex('create'));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSelectChange = (event: SelectChangeEvent<number>) => {
    setTabValue(Number(event.target.value));
  };

  const tabOptions = visibleTabs.map((tab, index) => ({label: tab.label, value: index}));

  if (!user) {
    return <LoggedOutView />
  }

  return (
    <PageWithHeader>
      <Box sx={{ width: '100%', p: { xs: 1.5, sm: 2.5 } }}>
        <Card raised sx={{ borderRadius: '20px', boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography variant='h3' sx={{color: '#345678', fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2rem' }}}>
                Visitor Management
              </Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              {isMobile ? (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel id="vms-section-label">Section</InputLabel>
                  <Select<number>
                    labelId="vms-section-label"
                    value={tabValue}
                    label="Section"
                    onChange={handleSelectChange}
                  >
                    {tabOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  aria-label="visitor management tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      textTransform: 'none',
                    },
                    '& .Mui-selected': {
                      color: '#345678',
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#345678',
                      height: 3,
                    }
                  }}
                >
                  {visibleTabs.map((tab) => (
                    <Tab key={tab.key} label={tab.label} />
                  ))}
                </Tabs>
              )}
            </Box>
            
            <TabPanel value={tabValue} index={tabIndex('create')}>
              <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                {!showCreatedInvite ? (
                  <AddVisitor
                    onSuccess={handleSuccess}
                    prefill={reinvitePrefill}
                  />
                ) : (
                  <Box sx={{
                    backgroundColor: '#f8f9fa',
                    p: 3,
                    borderRadius: '10px',
                    border: '1px solid #e9ecef'
                  }}>
                    <Typography variant="h5" gutterBottom color="#345678">
                      Invite Created Successfully
                    </Typography>
                    <InviteDetails
                      invite={createdInvite}
                      showCard={false}
                      showBanner={false}
                      showDownloadBtn={true}
                      showAddAnotherInviteBtn={true}
                      addAnotherInviteCallback={() => { setShowCreatedInvite(false); setReinvitePrefill(null); }}
                    />
                  </Box>
                )}
              </Box>
            </TabPanel>
            
            {isOperator && (
              <TabPanel value={tabValue} index={tabIndex('previous')}>
                <PreviousInvites onReinvite={handleReinvite} />
              </TabPanel>
            )}
            
            {isVmsUser && (
              <React.Fragment>
                <TabPanel value={tabValue} index={tabIndex('today')}>
                  <TodayVisitors />
                </TabPanel>
                
                <TabPanel value={tabValue} index={tabIndex('current')}>
                  <CurrentVisitors />
                </TabPanel>
                
                <TabPanel value={tabValue} index={tabIndex('scan')}>
                  <ScanInvite />
                </TabPanel>
              </React.Fragment>
            )}
            
            {canScanInvites && (
              <TabPanel value={tabValue} index={tabIndex('scan')}>
                <ScanInvite />
              </TabPanel>
            )}
          </CardContent>
        </Card>
      </Box>
    </PageWithHeader>
  )
}

export default VisitorManagementDashboard;